// API Configuration - Main backend (5000), Patch catalog/master on 5173
// In browser use relative /api/v1 so the same host can proxy to backend (avoids CORS "Failed to fetch")
function getApiBaseUrl(): string {
  if (typeof window !== "undefined") return `${window.location.origin}/api/v1`;
  return process.env.REACT_APP_API_URL || "/api/v1";
}
// Same origin in the browser as the main API. The patch endpoints are served by
// the main backend through nginx; port 5173 has no route there and is firewalled,
// so a host:port default would be blocked as mixed content and by connect-src.
function getPatchApiBaseUrl(): string {
  if (typeof window !== "undefined") return `${window.location.origin}/api/v1`;
  return process.env.REACT_APP_PATCH_API_URL || "/api/v1";
}
const API_BASE_URL = getApiBaseUrl();
const PATCH_API_BASE_URL = getPatchApiBaseUrl();

const TOKEN_KEY = 'uem_token';
const PORTAL_TENANT_KEY = 'platform_active_tenant_id';

function getAuthHeaders(): HeadersInit {
  try {
    const headers: Record<string, string> = {};
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) headers.Authorization = `Bearer ${t}`;
    const portalTenant = localStorage.getItem(PORTAL_TENANT_KEY);
    if (portalTenant) headers['X-Tenant-Id'] = portalTenant;
    return headers;
  } catch {}
  return {};
}

/** Shared fetch helper: handles response.ok, JSON/blob, optional fallback and error.detail. */
async function request<T>(
  url: string,
  init?: RequestInit,
  opts?: { asBlob?: boolean; fallback?: T; useErrorDetail?: boolean; logMessage?: string }
): Promise<T> {
  const headers = new Headers(init?.headers);
  // An explicit X-Tenant-Id on the call wins, so a caller can read one tenant
  // without switching the stored portal tenant.
  const explicitTenant = headers.get('X-Tenant-Id');
  Object.entries(getAuthHeaders()).forEach(([k, v]) => headers.set(k, v as string));
  if (explicitTenant) headers.set('X-Tenant-Id', explicitTenant);
  // POSTing JSON.stringify(...) without Content-Type makes FastAPI treat the body as a string → HTTP 422.
  const method = (init?.method || 'GET').toUpperCase();
  const hasBody = init?.body != null && method !== 'GET' && method !== 'HEAD';
  if (hasBody && !headers.has('Content-Type') && typeof init?.body === 'string') {
    headers.set('Content-Type', 'application/json');
  }
  const mergedInit = { ...init, headers };
  const useErrorDetail = opts?.useErrorDetail !== false;
  try {
    const response = await fetch(url, mergedInit);
    if (response.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem('uem_user');
      window.dispatchEvent(new CustomEvent('uem-unauthorized'));
      throw new Error('Session expired. Please sign in again.');
    }
    if (!response.ok) {
      let message = `HTTP ${response.status}: ${response.statusText}`;
      if (useErrorDetail) {
        try {
          const err = await response.json();
          const raw = err?.detail ?? err?.error;
          if (raw != null) {
            if (typeof raw === 'string') message = raw;
            else if (Array.isArray(raw) && raw.length > 0 && raw[0]?.msg) message = raw.map((e: any) => e.msg).join('; ');
            else if (typeof raw === 'object' && (raw.msg || raw.message)) message = String(raw.msg ?? raw.message);
            else message = typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
          }
        } catch {
          // ignore
        }
      }
      throw new Error(message);
    }
    if (opts?.asBlob) return response.blob() as Promise<T>;
    return response.json();
  } catch (e) {
    if (opts?.fallback !== undefined) {
      if (opts?.logMessage) console.error(opts.logMessage, e);
      return opts.fallback;
    }
    if (opts?.logMessage) console.error(opts.logMessage, e);
    throw e;
  }
}

/** Fetch from first base; on failure try second base (for patch catalog fallback). */
async function requestWithFallbackBase<T>(path: string, firstBase: string, secondBase: string, init?: RequestInit): Promise<T> {
  try {
    return await request<T>(firstBase + path, init);
  } catch (e) {
    try {
      return await request<T>(secondBase + path, init);
    } catch (fallback) {
      console.error("Failed to fetch (both bases):", e);
      throw e;
    }
  }
}

/** Header used to read a specific tenant without switching the active portal. */
function companionTenantHeader(tenantId?: string): Record<string, string> {
  return tenantId ? { 'X-Tenant-Id': tenantId } : {};
}

export interface Device {
  device_id: string;
  name: string;
  os: string;
  platform: string;
  status: string;
  compliance_status: string;
  last_seen: string;
  device_metadata?: any;
  cpu_usage?: number;
  memory_usage?: number;
  ip_address?: string;
  department?: string;
  device_type?: string;
}

export interface HealthStatus {
  status: string;
  database: string;
  version: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  items?: T[];
  error?: string;
  status_code?: number;
}

export interface User {
  id: string;
  username: string;
  full_name: string | null;
  email: string | null;
  role: string;
  scopes: string[];
  is_active: boolean;
  created_at: string | null;
}

// Go Agent interfaces
export interface GoAgent {
  device_id: string;
  name: string;
  os: string;
  platform: string;
  status: string;
  last_seen: string;
  created_at: string;
}

export interface GoAgentCommand {
  command_id: string;
  device_id: string;
  command_type: string;
  command: string;
  parameters?: any;
  status: string;
  result?: any;
  timeout_seconds: number;
  created_at: string;
  executed_at?: string;
  completed_at?: string;
  created_by: string;
  error_message?: string;
}

// Agent capability matrix
export type CapabilitySupport = "supported" | "unsupported" | "unknown";

export interface CapabilityState {
  key: string;
  support: CapabilitySupport;
  reason: string;
  detail: string;
}

export interface AgentCapabilityDefinition {
  key: string;
  label: string;
  description: string;
  platforms: string[];
  variants: string[];
  min_version: string | null;
}

export interface AgentCapabilityCatalog {
  current_agent_version: string;
  enforcement_mode: string;
  capabilities: AgentCapabilityDefinition[];
}

export interface DeviceCapabilityMatrix {
  device_id: string;
  device_name?: string;
  device_class?: string | null;
  agent_version: string | null;
  platform: string | null;
  build_variant: string | null;
  build_variant_source: string;
  source: string;
  capabilities: Record<string, CapabilityState>;
  supported: string[];
  unsupported: string[];
  unknown: string[];
}

export interface AgentCapabilitySummary {
  total_devices: number;
  devices_reporting_capabilities: number;
  current_agent_version: string;
  enforcement_mode: string;
  by_capability: Array<{
    key: string;
    label: string;
    supported: number;
    unsupported: number;
    unknown: number;
  }>;
  by_build_variant: Array<{ build_variant: string; count: number }>;
  by_agent_version: Array<{ agent_version: string; count: number }>;
  devices: Array<{
    device_id: string;
    device_name: string;
    os: string;
    device_class: string | null;
    agent_version: string | null;
    build_variant: string | null;
    support: CapabilitySupport;
    reason: string;
    detail: string;
  }>;
  capability: string | null;
}

// Osquery query library (osquery_queries / osquery_query_overrides)
export interface OsqueryLibraryQuery {
  id: string;
  tenant_id: string | null;
  name: string;
  sql: string;
  description: string | null;
  category: string;
  platforms: string[];
  interval_minutes: number;
  enabled: boolean;
  is_system: boolean;
  overridden: boolean;
  // Present only when this tenant has overridden a system query.
  system_default?: { enabled: boolean; interval_minutes: number };
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface OsqueryLibraryResponse {
  queries: OsqueryLibraryQuery[];
  categories: string[];
  platforms: string[];
  total: number;
}

// Patch Management (from device_metadata.patch_reports)
export interface PatchReport {
  package_name: string;
  current_version?: string;
  new_version?: string;
  severity?: string;
  cve_ids?: string[];
}

// Patch catalog (NVD / Microsoft / Linux releases by month)
export interface PatchCatalogItem {
  cve_id: string;
  title: string;
  severity: string;
  published_date: string;
  url: string;
}

export interface PatchCatalogResponse {
  items: PatchCatalogItem[];
  total: number;
  source: string;
  cached?: boolean;
  year?: number;
  month?: number;
  os?: string;
  severity_summary?: Record<string, number>;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

// Activity Log interfaces
export interface ActivityLog {
  id?: string;
  device_id: string;
  app_name: string;
  window_title?: string;
  url?: string;
  domain?: string;
  start_time: string;
  duration_seconds: number;
  is_productive?: boolean;
  user_id?: string;
}

export interface URLCategory {
  domain: string;
  category: string;
  subcategory?: string;
  productivity_score: number;
  is_blocked: boolean;
}

export interface ProductivityReport {
  device_id: string;
  user_id?: string;
  period_start: string;
  period_end: string;
  total_time_seconds: number;
  productive_time_seconds: number;
  productivity_percentage: number;
  category_breakdown: Record<string, { time_seconds: number; count: number }>;
  activities: ActivityLog[];
}

// API Service Functions
export const apiService = {
  // Health check
  async checkHealth(): Promise<HealthStatus | null> {
    return request<HealthStatus | null>(`${API_BASE_URL}/health`, undefined, {
      fallback: null,
      logMessage: "Health check failed",
    });
  },

  // Get devices
  async getDevices(): Promise<Device[]> {
    const data = await request<{ items?: Device[] }>(`${API_BASE_URL}/devices`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch devices",
    });
    return data.items ?? [];
  },

  // Get device compliance summary
  async getDeviceCompliance(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/compliance/summary`, undefined, {
      fallback: [],
      logMessage: "Failed to fetch compliance data",
    });
  },

  // Get device system information only
  async getDeviceSystemInfo(deviceId: string): Promise<any> {
    const data = await request<{ data?: any }>(`${API_BASE_URL}/devices/${deviceId}/system-info`, undefined, {
      fallback: { data: null },
      logMessage: "Failed to fetch system info",
    });
    return data.data ?? null;
  },

  // Get policies
  async getPolicies(): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/policies`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch policies",
    });
    return data.items ?? [];
  },

  // Get connectors
  async getConnectors(): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/connectors`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch connectors",
    });
    return data.items ?? [];
  },

  // Get audit logs
  async getAuditLogs(): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/audit`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch audit logs",
    });
    return data.items ?? [];
  },

  // Osquery/FleetDM endpoints
  async getOsqueryHosts(): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/osquery/hosts`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch osquery hosts",
    });
    return data.items ?? [];
  },

  async getOsqueryStatus(): Promise<any> {
    return request<any>(`${API_BASE_URL}/osquery/status`, undefined, {
      fallback: { osquery_connected: false, status: "unhealthy" },
      logMessage: "Failed to fetch osquery status",
    });
  },

  async syncDevicesWithOsquery(): Promise<any> {
    return request<any>(`${API_BASE_URL}/osquery/sync-devices`, undefined, {
      fallback: { error: "Sync failed" },
      logMessage: "Failed to sync devices with osquery",
    });
  },

  async enrollOsqueryAgent(payload: {
    hostname: string;
    platform?: string;
    os_version?: string;
    osquery_version?: string;
    device_id?: string;
    status?: string;
  }): Promise<any> {
    return request<any>(`${API_BASE_URL}/osquery/enroll`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  },

  // Policy management endpoints
  async getPolicyDeployments(): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/policies/deployments`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch policy deployments",
    });
    return data.items ?? [];
  },

  /** Live Policies & Compliance script deploys (device_commands), not legacy policy_deployments. */
  async getPolicyDeploymentStatus(opts?: {
    skip?: number;
    limit?: number;
    device_id?: string;
    outcome?: string;
  }): Promise<{ items: any[]; total: number; page?: number; size?: number; pages?: number }> {
    const params = new URLSearchParams();
    if (opts?.skip != null) params.set("skip", String(opts.skip));
    if (opts?.limit != null) params.set("limit", String(opts.limit));
    if (opts?.device_id) params.set("device_id", opts.device_id);
    if (opts?.outcome) params.set("outcome", opts.outcome);
    const qs = params.toString();
    const wrapped = await request<{ success?: boolean; data?: any }>(
      `${API_BASE_URL}/policies/deployment-status${qs ? `?${qs}` : ""}`,
      undefined,
      {
        fallback: { success: true, data: { items: [], total: 0 } },
        logMessage: "Failed to fetch policy deployment status",
      },
    );
    const data = wrapped?.data ?? wrapped;
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      total: typeof data?.total === "number" ? data.total : 0,
      page: data?.page,
      size: data?.size,
      pages: data?.pages,
    };
  },

  async createPolicy(policyData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/policies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(policyData),
    });
  },

  async deployPolicyToDevices(policyId: string, deviceIds: string[]): Promise<any> {
    return request<any>(`${API_BASE_URL}/policies/${policyId}/deploy`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_ids: deviceIds }),
    });
  },

  async updatePolicy(policyId: string, policyData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/policies/${policyId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(policyData),
    });
  },

  async deletePolicy(policyId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/policies/${policyId}`, { method: "DELETE" });
  },

  async enforcePolicyDeployment(deploymentId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/policies/deployments/${deploymentId}/enforce`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });
  },

  // Enhanced Device Management APIs
  async getDeviceGroups(): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/enhanced-devices/groups`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch device groups",
    });
    return data.items ?? [];
  },

  async getGroupDevices(groupId: string, skip = 0, limit = 100): Promise<{ items: any[]; total: number }> {
    const data = await request<{ items?: any[]; total?: number }>(
      `${API_BASE_URL}/enhanced-devices/groups/${encodeURIComponent(groupId)}/devices?skip=${skip}&limit=${limit}`,
      undefined,
      { fallback: { items: [], total: 0 }, logMessage: "Failed to fetch group devices" }
    );
    return { items: data.items ?? [], total: data.total ?? (data.items?.length ?? 0) };
  },

  async getDeviceLocations(deviceId: string): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/enhanced-devices/${deviceId}/locations`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch device locations",
    });
    return data.items ?? [];
  },

  async getDeviceHardware(deviceId: string): Promise<any[]> {
    const data = await request<any>(`${API_BASE_URL}/enhanced-devices/${deviceId}/hardware`, undefined, {
      fallback: null,
      logMessage: "Failed to fetch device hardware",
    });
    return Array.isArray(data) ? data : data ? [data] : [];
  },

  async getDeviceSoftware(deviceId: string): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/enhanced-devices/${deviceId}/software`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch device software",
    });
    return data.items ?? [];
  },

  async getDeviceUsers(deviceId: string): Promise<any[]> {
    const data = await request<any[] | { items?: any[] }>(`${API_BASE_URL}/enhanced-devices/${deviceId}/users`, undefined, {
      fallback: [],
      logMessage: "Failed to fetch device users",
    });
    return Array.isArray(data) ? data : (data?.items ?? []);
  },

  async getDeviceSecurity(deviceId: string): Promise<any[]> {
    const data = await request<any>(`${API_BASE_URL}/enhanced-devices/${deviceId}/security`, undefined, {
      fallback: null,
      logMessage: "Failed to fetch device security",
    });
    return Array.isArray(data) ? data : data ? [data] : [];
  },

  async getDevicePerformance(deviceId: string): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/enhanced-devices/${deviceId}/performance`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch device performance",
    });
    return data.items ?? [];
  },

  async getDeviceAlerts(deviceId: string): Promise<any[]> {
    const data = await request<{ items?: any[] }>(`${API_BASE_URL}/enhanced-devices/${deviceId}/alerts`, undefined, {
      fallback: { items: [] },
      logMessage: "Failed to fetch device alerts",
    });
    return data.items ?? [];
  },

  async getEnhancedDeviceDashboard(): Promise<any> {
    return request<any>(`${API_BASE_URL}/enhanced-devices/dashboard/overview`, undefined, {
      fallback: null,
      logMessage: "Failed to fetch enhanced device dashboard",
    });
  },

  // Reports and query execution endpoints
  async executeCustomQuery(query: string, queryType: string = "auto"): Promise<any> {
    return request<any>(`${API_BASE_URL}/reports/execute-query`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query, queryType }),
    });
  },

  async exportToExcel(data: any, filename: string): Promise<Blob> {
    return request<Blob>(`${API_BASE_URL}/reports/export-excel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data, filename }),
    }, { asBlob: true });
  },

  async getPolicyComplianceReport(): Promise<any> {
    return request<any>(`${API_BASE_URL}/reports/policy-compliance`);
  },

  async getDeviceInventoryReport(): Promise<any> {
    return request<any>(`${API_BASE_URL}/reports/device-inventory`);
  },

  async getSecurityAssessmentReport(): Promise<any> {
    return request<any>(`${API_BASE_URL}/reports/security-assessment`);
  },

  async getQueryTemplates(): Promise<any> {
    return request<any>(`${API_BASE_URL}/reports/query-templates`);
  },

  async getDeviceDetails(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/reports/device-details/${deviceId}`);
  },

  // Remote Management APIs
  async deployPolicy(policyData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/remote-management/deploy-policy`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(policyData),
    });
  },

  async executeScript(scriptData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/remote-management/execute-script`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(scriptData),
    });
  },

  async executeCommand(commandData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/remote-management/execute-command`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(commandData),
    });
  },

  async getPolicyTemplates(): Promise<any> {
    return request<any>(`${API_BASE_URL}/remote-management/policy-templates`);
  },

  async getScriptTemplates(): Promise<any> {
    return request<any>(`${API_BASE_URL}/remote-management/script-templates`);
  },

  async getDeploymentStatus(deploymentId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/remote-management/deployment-status/${deploymentId}`);
  },

  // Windows Home Management APIs
  async modifyRegistry(registryData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/wazuh/modify-registry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(registryData),
    });
  },

  async createUser(userData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/wazuh/create-user`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(userData),
    });
  },

  // ============================================
  // Mobile MDM (tenant-scoped via core :5000 → Buntu)
  // ============================================

  async listMobileMdmDevices(platform: "android" | "ios" = "android"): Promise<any[]> {
    const data = await request<any[]>(
      `${API_BASE_URL}/mobile-mdm/devices?platform=${encodeURIComponent(platform)}`,
      undefined,
      { fallback: [], logMessage: "Failed to list mobile MDM devices" },
    );
    return Array.isArray(data) ? data : [];
  },

  // Legacy name → core tenant-scoped list
  async getBuntuMDMDevices(): Promise<any[]> {
    return this.listMobileMdmDevices("android");
  },

  async getBuntuMDMDevice(deviceId: string): Promise<any> {
    const rows = await this.listMobileMdmDevices("android");
    return rows.find((d: any) => d.id === deviceId || d.device_id === deviceId || d.platform_device_id === deviceId) || null;
  },

  async getBuntuMDMPolicies(): Promise<any[]> {
    const data = await request<any[]>(`${API_BASE_URL}/mobile-mdm/policies`, undefined, {
      fallback: [],
      logMessage: "Failed to fetch mobile MDM policies",
    });
    return Array.isArray(data) ? data : [];
  },

  async createBuntuMDMPolicy(policy: { id: string; name: string; payload: any }): Promise<any> {
    // Policy create still hits Buntu via server-side when wired; for now surface clear error path
    return request<any>(`${API_BASE_URL}/mobile-mdm/policies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(policy),
    });
  },

  async assignBuntuMDMPolicy(deviceId: string, policyId: string): Promise<any> {
    return request<any>(
      `${API_BASE_URL}/mobile-mdm/devices/${encodeURIComponent(deviceId)}/policy?policy_id=${encodeURIComponent(policyId)}`,
      { method: "POST" },
    );
  },

  async getBuntuMDMDevicePolicy(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/mobile-mdm/devices/${encodeURIComponent(deviceId)}/compliance`, undefined, {
      fallback: null,
      logMessage: "Failed to fetch device policy/compliance",
    });
  },

  async queueBuntuMDMCommand(deviceId: string, commandType: string, payload?: any): Promise<any> {
    return request<any>(
      `${API_BASE_URL}/mobile-mdm/ios/${encodeURIComponent(deviceId)}/command?command_type=${encodeURIComponent(commandType)}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload ? JSON.stringify(payload) : undefined,
      },
    );
  },

  async getBuntuMDMCommandQueue(_deviceId: string): Promise<any> {
    // Queue inspection remains on Buntu; UI only needs success placeholder after command proxy
    return { success: true, note: "queued_via_core_proxy" };
  },

  async getIosProfileUrl(deviceId: string): Promise<{ profile_url: string; profile_json_url?: string }> {
    return request(`${API_BASE_URL}/mobile-mdm/ios/${encodeURIComponent(deviceId)}/profile-url`);
  },

  getBuntuMDMProfileUrl(deviceId: string): string {
    // Sync helper for UI links; prefer getIosProfileUrl for tenant-checked URL
    return `${API_BASE_URL}/mobile-mdm/ios/${encodeURIComponent(deviceId)}/profile-url`;
  },

  async lockAndroidDevice(deviceId: string, pin?: string): Promise<any> {
    const q = pin ? `?pin=${encodeURIComponent(pin)}` : "";
    return request<any>(`${API_BASE_URL}/mobile-mdm/devices/${encodeURIComponent(deviceId)}/lock${q}`, { method: "POST" });
  },

  async wipeAndroidDevice(deviceId: string, preserveSdCard: boolean = false): Promise<any> {
    return request<any>(
      `${API_BASE_URL}/mobile-mdm/devices/${encodeURIComponent(deviceId)}/wipe?preserve_sd_card=${preserveSdCard}`,
      { method: "POST" },
    );
  },

  async syncAndroidPolicy(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/mobile-mdm/devices/${encodeURIComponent(deviceId)}/sync-policy`, { method: "POST" });
  },

  async getAndroidDeviceCompliance(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/mobile-mdm/devices/${encodeURIComponent(deviceId)}/compliance`, undefined, {
      fallback: null,
      logMessage: "Failed to get Android device compliance",
    });
  },

  async listBuntuAnalyticsReports(): Promise<any[]> {
    const data = await request<{ reports?: any[] }>(`${API_BASE_URL}/analytics`, undefined, {
      fallback: { reports: [] },
      logMessage: "Failed to list analytics reports",
    });
    return Array.isArray(data?.reports) ? data.reports : [];
  },

  async getBuntuAnalyticsReport(reportId: string): Promise<any | null> {
    return request<any>(`${API_BASE_URL}/analytics/${reportId}`, undefined, {
      fallback: null,
      logMessage: "Failed to fetch analytics report",
    });
  },

  async triggerDeviceInventorySync(deviceId: string, scope: string[] = ["hardware", "software"], notes?: string): Promise<any> {
    // Queue inventory-sync on the core UEM agent command path (not Buntu MDM).
    return request<any>(`${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/inventory-sync`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        scope: scope.length ? scope : ["software"],
        triggered_by: "manual",
        notes,
      }),
    });
  },

  async getDeviceSyncHistory(deviceId: string, limit: number = 25): Promise<any[]> {
    const result = await request<{ data?: { history?: any[] } }>(
      `${API_BASE_URL}/devices/${encodeURIComponent(deviceId)}/sync-history?limit=${limit}`,
      undefined,
      { fallback: { data: {} }, logMessage: "Failed to fetch sync history" },
    );
    return result.data?.history ?? [];
  },

  // ============================================
  // Platform Admin / Multi-tenant API
  // ============================================

  async platformListTenants(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/tenants`, undefined, {
      fallback: [],
      logMessage: "Failed to list tenants",
    });
  },

  async platformCreateTenant(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async platformSuspendTenant(id: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/suspend`, { method: "POST", body: "{}" });
  },

  async platformActivateTenant(id: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/activate`, { method: "POST", body: "{}" });
  },

  /** Soft-delete a tenant. confirmCode must match the tenant code exactly. */
  async platformDeleteTenant(id: string, confirmCode: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}`, {
      method: "DELETE",
      body: JSON.stringify({ confirm_code: confirmCode }),
    });
  },

  async platformDashboardSummary(): Promise<any> {
    return request(`${API_BASE_URL}/platform/dashboard/summary`, undefined, { fallback: {} });
  },

  async platformDashboardClientSummary(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/dashboard/client-device-summary`, undefined, { fallback: [] });
  },

  async platformListSubscriptions(status?: string): Promise<any[]> {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<any[]>(`${API_BASE_URL}/platform/subscriptions${q}`, undefined, { fallback: [] });
  },

  async platformGetSubscription(tenantId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/subscription`);
  },

  async platformUpdateSubscription(tenantId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/subscription`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformRenewSubscription(tenantId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/subscription/renew`, { method: "POST", body: "{}" });
  },

  async platformExtendTrial(tenantId: string, days = 14): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/subscription/extend-trial?days=${days}`, {
      method: "POST",
      body: "{}",
    });
  },

  async platformListBilling(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/billing`, undefined, { fallback: [] });
  },

  async platformGetBilling(tenantId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/billing`);
  },

  async platformUpdateBilling(tenantId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/billing`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformListLicenseUsage(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/license-usage`, undefined, { fallback: [] });
  },

  async platformGetLicenseUsage(tenantId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/license-usage`);
  },

  async platformUpdateLicenseLimits(tenantId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/license-limits`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformEntitlementsMatrix(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/entitlements/matrix`, undefined, { fallback: [] });
  },

  async platformListUsers(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/users`, undefined, { fallback: [] });
  },

  async platformCreateUser(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/users`, { method: "POST", body: JSON.stringify(body) });
  },

  async platformUpdateUser(userId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/users/${userId}`, { method: "PUT", body: JSON.stringify(body) });
  },

  async platformDisableUser(userId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/users/${userId}`, { method: "DELETE" });
  },

  async platformAudit(limit = 100, tenantId?: string): Promise<any[]> {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (tenantId) qs.set("tenant_id", tenantId);
    return request<any[]>(`${API_BASE_URL}/platform/audit?${qs}`, undefined, { fallback: [] });
  },

  async platformSystemHealth(): Promise<any> {
    return request(`${API_BASE_URL}/platform/system-health`, undefined, { fallback: { overall: "unknown", services: [] } });
  },

  async platformGetSettings(): Promise<any> {
    return request(`${API_BASE_URL}/platform/settings`, undefined, { fallback: { settings: {} } });
  },

  async platformUpdateSettings(settings: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/settings`, {
      method: "PUT",
      body: JSON.stringify({ settings }),
    });
  },

  async platformGetTenant(id: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}`);
  },

  async platformUpdateTenant(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformUpdateModules(id: string, modules: Record<string, boolean>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/modules`, {
      method: "PUT",
      body: JSON.stringify({ modules }),
    });
  },

  async platformUpdateBranding(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/branding`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformUpdateEnrollmentSettings(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/enrollment-settings`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformUpdateSecurity(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/security`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformListTenantInvites(id: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/tenants/${id}/enrollment-invites`, undefined, {
      fallback: [],
      logMessage: "Failed to list tenant invites",
    });
  },

  async platformListTenantUsers(id: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/tenants/${id}/users`, undefined, {
      fallback: [],
      logMessage: "Failed to list tenant users",
    });
  },

  async platformCreateTenantUser(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/users`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async platformUpdateTenantUser(tenantId: string, userId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformDeleteTenantUser(tenantId: string, userId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/users/${userId}`, {
      method: "DELETE",
    });
  },

  async platformListTenantGroups(id: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/tenants/${id}/groups`, undefined, {
      fallback: [],
      logMessage: "Failed to list tenant groups",
    });
  },

  async platformCreateTenantGroup(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/groups`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async platformTenantAudit(id: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/platform/tenants/${id}/audit`, undefined, {
      fallback: [],
      logMessage: "Failed to load audit",
    });
  },

  async tenantMe(): Promise<any> {
    return request(`${API_BASE_URL}/tenant/me`);
  },

  async tenantListUsers(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/tenant/users`, undefined, { fallback: [], logMessage: "tenant users" });
  },

  async tenantCreateUser(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/users`, { method: "POST", body: JSON.stringify(body) });
  },

  async tenantUpdateUser(userId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/users/${userId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async tenantDeleteUser(userId: string): Promise<any> {
    return request(`${API_BASE_URL}/tenant/users/${userId}`, { method: "DELETE" });
  },

  async tenantListGroups(tenantId?: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/tenant/groups`, {
      headers: companionTenantHeader(tenantId),
    }, { fallback: [], logMessage: "tenant groups" });
  },

  async tenantCreateGroup(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/groups`, { method: "POST", body: JSON.stringify(body) });
  },

  async tenantSetGroupPolicy(groupId: string, policyId: string | null): Promise<any> {
    return request(`${API_BASE_URL}/tenant/groups/${groupId}/policy`, {
      method: "PUT",
      body: JSON.stringify({ policy_id: policyId }),
    });
  },

  async tenantSetUserPolicy(userId: string, policyId: string | null): Promise<any> {
    return request(`${API_BASE_URL}/tenant/users/${userId}/policy`, {
      method: "PUT",
      body: JSON.stringify({ policy_id: policyId }),
    });
  },

  async tenantGetCompliancePack(): Promise<any> {
    return request(`${API_BASE_URL}/tenant/compliance-pack`, undefined, {
      fallback: { rules: [], thresholds: {}, notes: null },
      logMessage: "tenant compliance pack",
    });
  },

  async tenantPutCompliancePack(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/compliance-pack`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformGetEntitlements(id: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/entitlements`);
  },

  async platformPutOsEntitlements(id: string, os: unknown[]): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/os-entitlements`, {
      method: "PUT",
      body: JSON.stringify({ os }),
    });
  },

  async platformPutPageEntitlements(id: string, pages: unknown[]): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/page-entitlements`, {
      method: "PUT",
      body: JSON.stringify({ pages }),
    });
  },

  async platformPutActionEntitlements(id: string, actions: unknown[]): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/action-entitlements`, {
      method: "PUT",
      body: JSON.stringify({ actions }),
    });
  },

  async tenantRbacListRoles(): Promise<any[]> {
    return request(`${API_BASE_URL}/tenant/rbac/roles`, undefined, { fallback: [], logMessage: "rbac roles" });
  },

  async tenantRbacAssignable(): Promise<any[]> {
    return request(`${API_BASE_URL}/tenant/rbac/assignable-permissions`, undefined, {
      fallback: [],
      logMessage: "assignable permissions",
    });
  },

  async tenantRbacCreateRole(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/rbac/roles`, { method: "POST", body: JSON.stringify(body) });
  },

  async tenantRbacUpdateRole(roleId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/rbac/roles/${roleId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async tenantRbacDeleteRole(roleId: string): Promise<any> {
    return request(`${API_BASE_URL}/tenant/rbac/roles/${roleId}`, { method: "DELETE" });
  },

  async tenantRbacSetUserRoles(userId: string, roleIds: string[]): Promise<any> {
    return request(`${API_BASE_URL}/tenant/rbac/users/${userId}/roles`, {
      method: "PUT",
      body: JSON.stringify({ role_ids: roleIds }),
    });
  },

  async tenantRbacGetUserRoles(userId: string): Promise<any[]> {
    return request(`${API_BASE_URL}/tenant/rbac/users/${userId}/roles`, undefined, {
      fallback: [],
      logMessage: "user roles",
    });
  },

  async tenantRbacTemplates(): Promise<any[]> {
    return request(`${API_BASE_URL}/tenant/rbac/templates`, undefined, { fallback: [], logMessage: "rbac templates" });
  },

  async platformGetCompliancePack(id: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/compliance-pack`, undefined, {
      fallback: { rules: [], thresholds: {}, notes: null },
      logMessage: "platform compliance pack",
    });
  },

  async platformPutCompliancePack(id: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${id}/compliance-pack`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async tenantListDevices(platform?: string): Promise<any[]> {
    const q = platform ? `?platform=${encodeURIComponent(platform)}` : "";
    return request<any[]>(`${API_BASE_URL}/tenant/devices${q}`, undefined, { fallback: [], logMessage: "tenant devices" });
  },

  async tenantListPolicies(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/tenant/policies`, undefined, { fallback: [], logMessage: "tenant policies" });
  },

  async tenantListInvites(tenantId?: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/tenant/enrollment-invites`, {
      headers: companionTenantHeader(tenantId),
    }, { fallback: [], logMessage: "tenant invites" });
  },

  async tenantEnrollmentCapacity(tenantId?: string): Promise<{
    max_devices: number | null;
    devices_in_use: number;
    remaining_seats: number | null;
  }> {
    return request(`${API_BASE_URL}/tenant/enrollment-capacity`, {
      headers: companionTenantHeader(tenantId),
    }, {
      fallback: { max_devices: null, devices_in_use: 0, remaining_seats: null },
      logMessage: "tenant enrollment capacity",
    });
  },

  async tenantCreateEnrollmentInvite(body: Record<string, unknown>, tenantId?: string): Promise<any> {
    return request(`${API_BASE_URL}/tenant/enrollment-invites`, {
      method: "POST",
      headers: companionTenantHeader(tenantId),
      body: JSON.stringify(body),
    });
  },

  async tenantRevokeEnrollmentInvite(inviteId: string, tenantId?: string): Promise<any> {
    return request(`${API_BASE_URL}/tenant/enrollment-invites/${inviteId}/revoke`, {
      method: "POST",
      headers: companionTenantHeader(tenantId),
    });
  },

  async importBuntuDevices(): Promise<any> {
    return request(`${API_BASE_URL}/enrollment/mobile/import-buntu`, { method: "POST" });
  },

  // Package repository + application catalog
  async getTenantPackageSettings(): Promise<any> {
    return request(`${API_BASE_URL}/tenant/package-settings`, undefined, {
      logMessage: "Failed to load package settings",
    });
  },

  async updateTenantPackageSettings(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/package-settings`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformGetPackageSettings(tenantId: string): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/package-settings`);
  },

  async platformUpdatePackageSettings(tenantId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/platform/tenants/${tenantId}/package-settings`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async listTenantApps(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/tenant/apps`, undefined, {
      fallback: [],
      logMessage: "Failed to list apps",
    });
  },

  async createTenantApp(body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/apps`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async uploadTenantApp(formData: FormData): Promise<any> {
    return request(`${API_BASE_URL}/tenant/apps/upload`, {
      method: "POST",
      body: formData,
    });
  },

  async updateTenantApp(appId: string, formData: FormData): Promise<any> {
    return request(`${API_BASE_URL}/tenant/apps/${appId}`, {
      method: "PUT",
      body: formData,
    });
  },

  async deleteTenantApp(appId: string): Promise<any> {
    return request(`${API_BASE_URL}/tenant/apps/${appId}`, { method: "DELETE" });
  },

  async deployTenantApp(appId: string, body: Record<string, unknown>): Promise<any> {
    return request(`${API_BASE_URL}/tenant/apps/${appId}/deploy`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async listTenantAppDeployments(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/tenant/app-deployments`, undefined, {
      fallback: [],
      logMessage: "Failed to list deployments",
    });
  },

  async getTenantAppDeployment(id: string): Promise<any> {
    return request(`${API_BASE_URL}/tenant/app-deployments/${id}`);
  },

  // Tenant directory (folders / collections / policy library) — DB-backed
  async getTenantDirectory(): Promise<{ folders: any[]; collections: any[] }> {
    return request(`${API_BASE_URL}/tenant/directory`, undefined, {
      fallback: { folders: [], collections: [] },
      logMessage: "Failed to fetch tenant directory",
    });
  },

  async putTenantDirectory(body: {
    folders: Array<{ id: string; name: string; isDefault?: boolean }>;
    collections: Array<{
      id: string;
      name: string;
      folderId?: string | null;
      deviceIds?: string[];
      criticalDeviceIds?: string[];
      createdAt?: string;
    }>;
  }): Promise<{ folders: any[]; collections: any[] }> {
    return request(`${API_BASE_URL}/tenant/directory`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async importTenantDirectory(body: {
    folders?: any[];
    collections?: any[];
    policy_library?: { general?: Record<string, unknown>; baselines?: Record<string, unknown> };
    replace?: boolean;
  }): Promise<{ folders: any[]; collections: any[] }> {
    return request(`${API_BASE_URL}/tenant/directory/import-local`, {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  async getTenantPolicyLibrary(): Promise<{ general: Record<string, unknown>; baselines: Record<string, unknown> }> {
    return request(`${API_BASE_URL}/tenant/policy-library`, undefined, {
      fallback: { general: {}, baselines: {} },
      logMessage: "Failed to fetch policy library",
    });
  },

  async putTenantPolicyLibrary(body: {
    general?: Record<string, unknown>;
    baselines?: Record<string, unknown>;
  }): Promise<{ general: Record<string, unknown>; baselines: Record<string, unknown> }> {
    return request(`${API_BASE_URL}/tenant/policy-library`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  // ============================================
  // Go Agent Management API
  // ============================================
  
  // Get all Go agents
  async getGoAgents(params?: { status?: string; search?: string; page?: number; size?: number }): Promise<PaginatedResponse<GoAgent>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.search) queryParams.append("search", params.search);
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.size) queryParams.append("size", params.size.toString());
    const url = `${API_BASE_URL}/go-agent/agents${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    return request<PaginatedResponse<GoAgent>>(url, undefined, {
      fallback: { items: [], total: 0, page: 1, size: 10, pages: 0 },
      logMessage: "Failed to fetch Go agents",
    });
  },

  async getGoAgentStatus(deviceId: string): Promise<any> {
    const data = await request<{ data?: any }>(`${API_BASE_URL}/go-agent/${deviceId}/status`, undefined, {
      fallback: { data: null },
      logMessage: "Failed to fetch Go agent status",
    });
    return data.data ?? null;
  },

  async executeGoAgentCommand(deviceId: string, command: {
    command_type: string;
    command: string;
    parameters?: any;
    timeout_seconds?: number;
    created_by?: string;
  }): Promise<any> {
    const data = await request<{ data?: any }>(`${API_BASE_URL}/go-agent/${deviceId}/execute`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(command),
    }, { useErrorDetail: true });
    return data.data ?? null;
  },

  async getGoAgentCommands(deviceId: string, params?: { status?: string; command_type?: string; limit?: number; skip?: number }): Promise<PaginatedResponse<GoAgentCommand>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.command_type) queryParams.append("command_type", params.command_type);
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.skip) queryParams.append("skip", params.skip.toString());
    const url = `${API_BASE_URL}/go-agent/${deviceId}/commands${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    const data = await request<any>(url, undefined, {
      fallback: {},
      logMessage: "Failed to fetch Go agent commands",
    });
    const items = Array.isArray(data?.items) ? data.items : (data?.data?.items ?? []);
    return { items, total: data?.total ?? data?.data?.total ?? 0, page: data?.page ?? data?.data?.page ?? 1, size: data?.size ?? data?.data?.size ?? 10, pages: data?.pages ?? data?.data?.pages ?? 0 };
  },

  async deleteGoAgent(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/go-agent/agents/${deviceId}`, { method: "DELETE" }, { useErrorDetail: true });
  },

  async updateDeviceDepartment(deviceId: string, department: string): Promise<any> {
    const device = await this.getDevice(deviceId);
    const currentMetadata = device.device_metadata || {};
    return request<any>(`${API_BASE_URL}/devices/${deviceId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_metadata: { ...currentMetadata, department } }),
    }, { useErrorDetail: true });
  },

  async getPatchesCatalogReleases(os: "windows" | "linux", year: number, month: number): Promise<PatchCatalogResponse> {
    const path = `/patches/catalog/releases?os=${encodeURIComponent(os)}&year=${year}&month=${month}`;
    return requestWithFallbackBase<PatchCatalogResponse>(path, PATCH_API_BASE_URL, API_BASE_URL);
  },

  async submitPatchScanResults(deviceId: string, osFamily: string, reports: PatchReport[], osVersion?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/scan-results`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: deviceId, os_family: osFamily, os_version: osVersion, reports }),
    });
  },

  async getPatchMaster(params?: { source?: string; severity?: string; classification?: string; group_id?: string; year?: number; month?: number; limit?: number; offset?: number }): Promise<any> {
    const clean: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "" && String(v) !== "undefined") clean[k] = String(v);
      });
    }
    const q = new URLSearchParams(clean).toString();
    const path = `/patches/master${q ? "?" + q : ""}`;
    // Prefer Hub API (:5000); optional standalone patch API (:5173) as fallback only
    return requestWithFallbackBase<any>(path, API_BASE_URL, PATCH_API_BASE_URL);
  },

  async getDevicePatchStatus(deviceId: string, statusFilter?: string): Promise<any> {
    const q = statusFilter ? `?status_filter=${encodeURIComponent(statusFilter)}` : "";
    return request<any>(`${API_BASE_URL}/patches/device/${deviceId}/status${q}`);
  },

  async triggerDevicePatchScan(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/devices/${encodeURIComponent(deviceId)}/scan`, {
      method: "POST",
    });
  },

  async setPatchApproval(patchId: string, groupId: string, approvalStatus: "Approved" | "Declined" | "Not_Set"): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/approvals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ patch_id: patchId, group_id: groupId, approval_status: approvalStatus }),
    });
  },

  async getPatchSyncStatus(): Promise<any> {
    return requestWithFallbackBase<any>("/patches/sync-status", API_BASE_URL, PATCH_API_BASE_URL);
  },

  async triggerPatchSync(): Promise<any> {
    return requestWithFallbackBase<any>("/patches/sync", API_BASE_URL, PATCH_API_BASE_URL, { method: "POST" });
  },

  async triggerPatchInstall(deviceId: string, patchIds: string[], opts?: { groupId?: string; jobName?: string }): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/install`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        device_id: deviceId,
        patch_ids: patchIds,
        group_id: opts?.groupId || "",
        job_name: opts?.jobName,
      }),
    });
  },

  async getRequiredPatches(params?: { os?: string; severity?: string; group_id?: string; limit?: number }): Promise<any> {
    const clean: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "" && String(v) !== "undefined") clean[k] = String(v);
      });
    }
    const q = new URLSearchParams(clean).toString();
    return request<any>(`${API_BASE_URL}/patches/required${q ? "?" + q : ""}`);
  },

  async getPatchDeployments(limit: number = 50): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/deployments?limit=${limit}`);
  },

  async getPatchDeployment(jobId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/deployments/${jobId}`);
  },

  async getPatchSettings(): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/settings`);
  },

  async updatePatchSettings(settings: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/patches/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(settings),
    });
  },

  // Buntu companion (tenant policy + org messages)
  async getBuntuSettings(): Promise<any> {
    return request<any>(`${API_BASE_URL}/buntu/settings`);
  },

  async updateBuntuSettings(config: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/buntu/settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(config),
    });
  },

  async getBuntuRuntime(): Promise<any> {
    return request<any>(`${API_BASE_URL}/buntu/runtime`, undefined, {
      fallback: null,
      logMessage: "Buntu runtime unavailable",
    });
  },

  async listBuntuMessages(status?: string): Promise<any> {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<any>(`${API_BASE_URL}/buntu/messages${q}`);
  },

  async createBuntuMessage(body: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/buntu/messages`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  async cancelBuntuMessage(messageId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/buntu/messages/${messageId}/cancel`, {
      method: "POST",
    });
  },

  async getBuntuInbox(): Promise<any> {
    return request<any>(`${API_BASE_URL}/buntu/inbox`, undefined, {
      fallback: { items: [] },
      logMessage: "Buntu inbox unavailable",
    });
  },

  async ackBuntuMessage(messageId: string, dismiss = true): Promise<any> {
    return request<any>(
      `${API_BASE_URL}/buntu/inbox/${messageId}/ack?dismiss=${dismiss ? "true" : "false"}`,
      { method: "POST" }
    );
  },

  // Buntu Companion portal — tenant admin scope.
  // tenantId is only passed by the platform (super admin) view, which reads one
  // tenant at a time without entering that tenant's portal.
  async getCompanionSettings(tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/settings`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async updateCompanionSettings(config: Record<string, any>, tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/settings`, {
      method: "PUT",
      headers: companionTenantHeader(tenantId),
      body: JSON.stringify(config),
    });
  },

  async getCompanionOverview(tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/overview`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async getCompanionAdoption(tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/adoption`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async listCompanionInstallations(
    params?: { status?: string; os_type?: string },
    tenantId?: string
  ): Promise<any> {
    const q = new URLSearchParams();
    if (params?.status) q.set("status", params.status);
    if (params?.os_type) q.set("os_type", params.os_type);
    const suffix = q.toString() ? `?${q.toString()}` : "";
    return request<any>(`${API_BASE_URL}/companion/admin/installations${suffix}`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async updateCompanionInstallation(installationId: string, status: string, tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/installations/${installationId}`, {
      method: "PUT",
      headers: companionTenantHeader(tenantId),
      body: JSON.stringify({ status }),
    });
  },

  async revokeCompanionSessions(installationId: string, tenantId?: string): Promise<any> {
    return request<any>(
      `${API_BASE_URL}/companion/admin/installations/${installationId}/revoke-sessions`,
      { method: "POST", headers: companionTenantHeader(tenantId) }
    );
  },

  async getCompanionVersions(tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/versions`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async listCompanionTickets(status?: string, tenantId?: string): Promise<any> {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<any>(`${API_BASE_URL}/companion/admin/support-tickets${q}`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async updateCompanionTicket(ticketId: string, body: Record<string, any>, tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/support-tickets/${ticketId}`, {
      method: "PUT",
      headers: companionTenantHeader(tenantId),
      body: JSON.stringify(body),
    });
  },

  async listCompanionNotifications(tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/notifications`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async createCompanionNotification(body: Record<string, any>, tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/notifications`, {
      method: "POST",
      headers: companionTenantHeader(tenantId),
      body: JSON.stringify(body),
    });
  },

  async listCompanionConsents(status?: string, tenantId?: string): Promise<any> {
    const q = status ? `?status=${encodeURIComponent(status)}` : "";
    return request<any>(`${API_BASE_URL}/companion/admin/remote-consents${q}`, {
      headers: companionTenantHeader(tenantId),
    });
  },

  async requestCompanionConsent(body: Record<string, any>, tenantId?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/companion/admin/remote-consents`, {
      method: "POST",
      headers: companionTenantHeader(tenantId),
      body: JSON.stringify(body),
    });
  },

  // Buntu Companion portal — platform (super admin) scope
  async getCompanionPlatformDefaults(): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/companion/defaults`);
  },

  async updateCompanionPlatformDefaults(config: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/companion/defaults`, {
      method: "PUT",
      body: JSON.stringify(config),
    });
  },

  async listCompanionPlatformTenants(): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/companion/tenants`);
  },

  async getCompanionTenantSettings(tenantId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/companion/tenants/${tenantId}/settings`);
  },

  async updateCompanionTenantSettings(tenantId: string, config: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/companion/tenants/${tenantId}/settings`, {
      method: "PUT",
      body: JSON.stringify(config),
    });
  },

  async updateCompanionTenantControls(tenantId: string, body: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/companion/tenants/${tenantId}/controls`, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  },

  async platformBuntuAdminTargets(): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/buntu/admin-targets`);
  },

  async platformBuntuBroadcastAdmins(body: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/buntu/broadcast-admins`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  async platformBuntuBroadcast(body: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/buntu/broadcast`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  async platformListBuntuMessages(limit = 100): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/buntu/messages?limit=${limit}`);
  },

  async platformCancelBuntuMessage(messageId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/platform/buntu/messages/${messageId}/cancel`, {
      method: "POST",
    });
  },

  async platformGetTenantBuntuSettings(tenantId: string): Promise<any> {
    const res = await request<any>(`${API_BASE_URL}/platform/tenants/${tenantId}/buntu-settings`);
    return res?.data ?? res;
  },

  async platformUpdateTenantBuntuSettings(
    tenantId: string,
    body: { enabled?: boolean; required_for_admins?: boolean }
  ): Promise<any> {
    const res = await request<any>(`${API_BASE_URL}/platform/tenants/${tenantId}/buntu-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    return res?.data ?? res;
  },

  // Reboot Management
  async getRebootStatusSummary(): Promise<any> {
    return request<any>(`${API_BASE_URL}/reboot/status/summary`);
  },

  async getRebootPolicies(): Promise<any> {
    return request<any>(`${API_BASE_URL}/reboot/policies`);
  },

  async createRebootPolicy(data: { name: string; type: string; config?: Record<string, any>; is_active?: boolean }): Promise<any> {
    return request<any>(`${API_BASE_URL}/reboot/policies`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  async assignRebootPolicy(policyId: string, groupId: string = ""): Promise<any> {
    return request<any>(`${API_BASE_URL}/reboot/policies/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ policy_id: policyId, group_id: groupId }),
    });
  },

  async triggerRebootRemind(deviceIds?: string[]): Promise<any> {
    return request<any>(`${API_BASE_URL}/reboot/remind`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_ids: deviceIds || [] }),
    });
  },

  async getDevice(deviceId: string): Promise<Device> {
    return request<Device>(`${API_BASE_URL}/devices/${deviceId}`, undefined, { logMessage: "Failed to fetch device" });
  },

  async getCollectionSettings(): Promise<any> {
    return request<any>(`${API_BASE_URL}/settings/collection`, undefined, { logMessage: "Failed to fetch collection settings" });
  },

  async updateCollectionSettings(settings: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/settings/collection`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...settings, updated_by: "admin" }),
    }, { useErrorDetail: true });
  },

  async getOsquerySettings(): Promise<any> {
    return request<any>(`${API_BASE_URL}/settings/osquery`, undefined, { logMessage: "Failed to fetch Osquery settings", useErrorDetail: true });
  },

  async updateOsquerySettings(settings: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/settings/osquery`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ settings, updated_by: "admin" }),
    }, { useErrorDetail: true });
  },

  async getOsqueryLibrary(params?: { category?: string; platform?: string }): Promise<OsqueryLibraryResponse> {
    const qs = new URLSearchParams();
    if (params?.category) qs.set("category", params.category);
    if (params?.platform) qs.set("platform", params.platform);
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    return request<OsqueryLibraryResponse>(`${API_BASE_URL}/osquery/library${suffix}`, undefined, {
      logMessage: "Failed to fetch Osquery library",
      useErrorDetail: true,
    });
  },

  async createOsqueryLibraryQuery(query: Partial<OsqueryLibraryQuery>): Promise<OsqueryLibraryQuery> {
    return request<OsqueryLibraryQuery>(`${API_BASE_URL}/osquery/library`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(query),
    }, { useErrorDetail: true });
  },

  async updateOsqueryLibraryQuery(id: string, updates: Partial<OsqueryLibraryQuery>): Promise<OsqueryLibraryQuery> {
    return request<OsqueryLibraryQuery>(`${API_BASE_URL}/osquery/library/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    }, { useErrorDetail: true });
  },

  async deleteOsqueryLibraryQuery(id: string): Promise<{ deleted: boolean }> {
    return request<{ deleted: boolean }>(`${API_BASE_URL}/osquery/library/${id}`, {
      method: "DELETE",
    }, { useErrorDetail: true });
  },

  async getOsqueryResolvedQueries(platform: string): Promise<{ platform: string; queries: { name: string; sql: string; interval_minutes: number }[]; total: number }> {
    return request(`${API_BASE_URL}/osquery/library/resolved?platform=${encodeURIComponent(platform)}`, undefined, {
      logMessage: "Failed to resolve Osquery queries",
      useErrorDetail: true,
    });
  },

  async getOsqueryHealthSummary(): Promise<{ by_device: Record<string, { cpu?: number; ram?: number; storage?: number; has_osquery: boolean }> }> {
    const data = await request<{ data?: { by_device?: Record<string, unknown> } }>(`${API_BASE_URL}/telemetry/osquery-health-summary`, undefined, {
      fallback: { data: {} },
      logMessage: "Failed to fetch Osquery health summary",
    });
    return { by_device: (data?.data?.by_device ?? {}) as Record<string, { cpu?: number; ram?: number; storage?: number; has_osquery: boolean }> };
  },

  /** Diagnostic: snapshot counts and device list (last 24h) for Settings → Osquery. */
  async getOsqueryTelemetryStatus(): Promise<{
    snapshot_count_total: number;
    snapshot_count_last_24h: number;
    device_ids_with_snapshots_last_24h: string[];
    hint?: string;
  }> {
    const data = await request<{ data?: any }>(`${API_BASE_URL}/telemetry/osquery-status`, undefined, {
      fallback: { data: { snapshot_count_total: 0, snapshot_count_last_24h: 0, device_ids_with_snapshots_last_24h: [] } },
      logMessage: "Failed to fetch Osquery status",
    });
    const d = data?.data ?? {};
    return {
      snapshot_count_total: Number(d.snapshot_count_total) || 0,
      snapshot_count_last_24h: Number(d.snapshot_count_last_24h) || 0,
      device_ids_with_snapshots_last_24h: Array.isArray(d.device_ids_with_snapshots_last_24h) ? d.device_ids_with_snapshots_last_24h : [],
      hint: d.hint,
    };
  },

  async getOsquerySnapshots(deviceId: string, limit = 50, queryName?: string): Promise<{ snapshots: any[] }> {
    let url = `${API_BASE_URL}/telemetry/${deviceId}/osquery-snapshots?limit=${limit}`;
    if (queryName) url += `&query_name=${encodeURIComponent(queryName)}`;
    const data = await request<{ data?: { snapshots?: any[] } }>(url, undefined, {
      fallback: { data: {} },
      logMessage: "Failed to fetch Osquery snapshots",
    });
    return { snapshots: data?.data?.snapshots ?? [] };
  },

  async getAgentConfig(deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/settings/agent-config/${deviceId}`, undefined, { logMessage: "Failed to fetch agent config" });
  },

  async getEnrollmentServer(): Promise<{ enrollment_server_host_port: string; enrollment_server_url: string }> {
    const data = await request<{ data?: any }>(`${API_BASE_URL}/settings/enrollment-server`, undefined, {
      fallback: { data: { enrollment_server_host_port: "localhost:7070", enrollment_server_url: "http://localhost:7070" } },
      logMessage: "Failed to fetch enrollment server",
    });
    const d = data?.data ?? {};
    return {
      enrollment_server_host_port: d.enrollment_server_host_port ?? "localhost:7070",
      enrollment_server_url: d.enrollment_server_url ?? "http://localhost:7070",
    };
  },

  async getUpgradeAgentScript(osFamily: "windows" | "linux"): Promise<string> {
    const osParam = osFamily === "linux" ? "linux" : "windows";
    const url = `${API_BASE_URL}/settings/upgrade-agent-script?os_family=${encodeURIComponent(osParam)}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to load upgrade script: ${res.status}`);
    return res.text();
  },

  async getDeviceSummaryAnalytics(): Promise<any> {
    return request<any>(`${API_BASE_URL}/analytics/device-summary`, undefined, { logMessage: "Failed to fetch device summary analytics" });
  },

  async getPlatformDistributionAnalytics(): Promise<any> {
    return request<any>(`${API_BASE_URL}/analytics/platform-distribution`, undefined, { logMessage: "Failed to fetch platform distribution analytics" });
  },

  async getComplianceStatusAnalytics(): Promise<any> {
    return request<any>(`${API_BASE_URL}/analytics/compliance-status`, undefined, { logMessage: "Failed to fetch compliance status analytics" });
  },

  async getHardwareInventoryAnalytics(skip: number = 0, limit: number = 100): Promise<any> {
    return request<any>(`${API_BASE_URL}/analytics/hardware-inventory?skip=${skip}&limit=${limit}`, undefined, { logMessage: "Failed to fetch hardware inventory analytics" });
  },

  async getActivityTrackingAnalytics(
    user_id?: string,
    device_id?: string,
    hours: number = 24,
    skip: number = 0,
    limit: number = 100
  ): Promise<ApiResponse<any>> {
    const params = new URLSearchParams({ hours: hours.toString(), skip: skip.toString(), limit: limit.toString() });
    if (user_id) params.append("user_id", user_id);
    if (device_id) params.append("device_id", device_id);
    return request<ApiResponse<any>>(`${API_BASE_URL}/analytics/activity-tracking?${params.toString()}`, undefined, {
      logMessage: "Failed to fetch activity tracking analytics",
    });
  },

  async getSoftwareInventoryAnalytics(skip: number = 0, limit: number = 100): Promise<any> {
    return request<any>(`${API_BASE_URL}/analytics/software-inventory?skip=${skip}&limit=${limit}`, undefined, {
      logMessage: "Failed to fetch software inventory analytics",
    });
  },

  // Productivity Tracking APIs
  async createActivityLogs(deviceId: string, activities: ActivityLog[]): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/telemetry/${deviceId}/activity`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ activities }),
    }, { logMessage: "Failed to create activity logs" });
  },

  async getProductivityReport(deviceId: string, hours: number = 8): Promise<ProductivityReport> {
    return request<ProductivityReport>(`${API_BASE_URL}/productivity/report/${deviceId}?hours=${hours}`, undefined, {
      logMessage: "Failed to get productivity report",
    });
  },

  async getURLCategories(): Promise<URLCategory[]> {
    return request<URLCategory[]>(`${API_BASE_URL}/admin/categories`, undefined, { logMessage: "Failed to get URL categories" });
  },

  async updateURLCategory(category: Partial<URLCategory>): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/admin/categories`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(category),
    }, { logMessage: "Failed to update URL category" });
  },

  // Agent Settings APIs (typed wrappers around existing calls)
  async getGroupCollectionSettings(groupId: string): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/settings/collection/group/${groupId}`, undefined, {
      logMessage: "Failed to get group collection settings",
    });
  },

  async updateGroupCollectionSettings(groupId: string, settings: any, updatedBy: string = "admin"): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/settings/collection/group/${groupId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ settings, updated_by: updatedBy }),
    }, { logMessage: "Failed to update group collection settings" });
  },

  async getDeviceCollectionSettings(deviceId: string): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/settings/collection/device/${deviceId}`, undefined, {
      logMessage: "Failed to get device collection settings",
    });
  },

  async updateDeviceCollectionSettings(deviceId: string, settings: any, updatedBy: string = "admin"): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/settings/collection/device/${deviceId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ settings, updated_by: updatedBy }),
    }, { logMessage: "Failed to update device collection settings" });
  },

  async getOsCollectionSettings(osName: string): Promise<ApiResponse<any>> {
    return request<ApiResponse<any>>(`${API_BASE_URL}/settings/collection/os/${osName}`, undefined, {
      logMessage: "Failed to get OS collection settings",
    });
  },

  async updateOSCollectionSettings(osName: string, settings: any, updatedBy: string = "admin"): Promise<any> {
    return request<any>(`${API_BASE_URL}/settings/collection/os/${osName}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ settings, updated_by: updatedBy }),
    }, { logMessage: "Failed to update OS collection settings" });
  },

  /** Alias for UI (updateOsCollectionSettings). */
  async updateOsCollectionSettings(osName: string, settings: any, updatedBy: string = "admin"): Promise<any> {
    return this.updateOSCollectionSettings(osName, settings, updatedBy);
  },

  // License Management APIs
  async getLicenses(params?: { skip?: number; limit?: number; search?: string; vendor?: string; license_type?: string; status?: string; expiry_status?: string }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.vendor) queryParams.append("vendor", params.vendor);
    if (params?.license_type) queryParams.append("license_type", params.license_type);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.expiry_status) queryParams.append("expiry_status", params.expiry_status);
    return request<PaginatedResponse<any>>(`${API_BASE_URL}/licenses?${queryParams}`, undefined, { logMessage: "Failed to fetch licenses" });
  },

  async getLicense(licenseId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/licenses/${licenseId}`, undefined, { logMessage: "Failed to fetch license" });
  },

  async createLicense(licenseData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/licenses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(licenseData),
    }, { useErrorDetail: true });
  },

  async updateLicense(licenseId: string, licenseData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/licenses/${licenseId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(licenseData),
    }, { useErrorDetail: true });
  },

  async deleteLicense(licenseId: string): Promise<void> {
    return request<void>(`${API_BASE_URL}/licenses/${licenseId}`, { method: "DELETE" }, { useErrorDetail: true });
  },

  async getLicenseAssignments(licenseId: string, params?: { skip?: number; limit?: number; status?: string }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params?.status) queryParams.append("status", params.status);
    return request<PaginatedResponse<any>>(`${API_BASE_URL}/licenses/${licenseId}/assignments?${queryParams}`, undefined, { logMessage: "Failed to fetch license assignments" });
  },

  async assignLicense(licenseId: string, assignmentData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/licenses/${licenseId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(assignmentData),
    }, { useErrorDetail: true });
  },

  async releaseLicenseAssignment(assignmentId: string): Promise<void> {
    return request<void>(`${API_BASE_URL}/licenses/assignments/${assignmentId}/release`, { method: "POST" }, { useErrorDetail: true });
  },

  async renewLicense(licenseId: string, renewalData: any): Promise<any> {
    return request<any>(`${API_BASE_URL}/licenses/${licenseId}/renew`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(renewalData),
    }, { useErrorDetail: true });
  },

  async getLicenseRenewals(licenseId: string, params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    return request<PaginatedResponse<any>>(`${API_BASE_URL}/licenses/${licenseId}/renewals?${queryParams}`, undefined, { logMessage: "Failed to fetch license renewals" });
  },

  async getExpiringLicenses(days: number = 90, params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    queryParams.append("days", days.toString());
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    return request<PaginatedResponse<any>>(`${API_BASE_URL}/licenses/expiring/list?${queryParams}`, undefined, { logMessage: "Failed to fetch expiring licenses" });
  },

  async getRenewalReminders(params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    return request<PaginatedResponse<any>>(`${API_BASE_URL}/licenses/renewal-reminders/list?${queryParams}`, undefined, { logMessage: "Failed to fetch renewal reminders" });
  },

  async bulkImportLicenses(file: File): Promise<any> {
    const formData = new FormData();
    formData.append("file", file);
    return request<any>(`${API_BASE_URL}/licenses/bulk-import`, { method: "POST", body: formData }, { useErrorDetail: true });
  },

  async exportLicenses(format: "csv" | "xlsx" | "json" = "xlsx"): Promise<Blob> {
    return request<Blob>(`${API_BASE_URL}/licenses/export?format=${format}`, undefined, { asBlob: true, logMessage: "Failed to export licenses" });
  },

  // ==========================================================================
  // COMPLIANCE RULES API (Rule Library for Compliance Hub)
  // ==========================================================================

  async getComplianceRuleCategorySummary(): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/categories/summary`, undefined, { logMessage: "Failed to fetch compliance category summary" });
  },

  async getComplianceRules(params?: {
    category?: string;
    check_type?: string;
    severity?: string;
    platform?: string;
    enabled?: boolean;
    search?: string;
    skip?: number;
    limit?: number;
  }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.category) queryParams.append("category", params.category);
    if (params?.check_type) queryParams.append("check_type", params.check_type);
    if (params?.severity) queryParams.append("severity", params.severity);
    if (params?.platform) queryParams.append("platform", params.platform);
    if (params?.enabled !== undefined) queryParams.append("enabled", params.enabled.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    const url = `${API_BASE_URL}/compliance-rules/${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    return request<PaginatedResponse<any>>(url, undefined, {
      fallback: { items: [], total: 0, page: 1, size: 100, pages: 0 },
      logMessage: "Failed to fetch compliance rules",
    });
  },

  async getComplianceRulesByCategory(category: string, includeResults: boolean = false): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/categories/${category}/rules?include_results=${includeResults}`, undefined, {
      logMessage: `Failed to fetch rules for category ${category}`,
    });
  },

  async getComplianceRule(ruleId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/${ruleId}`, undefined, { logMessage: `Failed to fetch compliance rule ${ruleId}` });
  },

  async createComplianceRule(rule: {
    title: string;
    description?: string;
    category: string;
    check_type: string;
    severity?: string;
    platform?: string;
    benchmark_ref?: string;
    check_command?: string;
    expected_value?: string;
    remediation_script?: string;
    remediation_steps?: string;
    enabled?: boolean;
    auto_remediate?: boolean;
  }): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(rule),
    }, { useErrorDetail: true });
  },

  async updateComplianceRule(ruleId: string, updates: Record<string, any>): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/${ruleId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(updates),
    }, { useErrorDetail: true });
  },

  async deleteComplianceRule(ruleId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/${ruleId}`, { method: "DELETE" }, { useErrorDetail: true });
  },

  async getComplianceResults(params?: {
    device_id?: string;
    rule_id?: string;
    category?: string;
    status?: string;
    skip?: number;
    limit?: number;
  }): Promise<PaginatedResponse<any>> {
    const queryParams = new URLSearchParams();
    if (params?.device_id) queryParams.append("device_id", params.device_id);
    if (params?.rule_id) queryParams.append("rule_id", params.rule_id);
    if (params?.category) queryParams.append("category", params.category);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.skip !== undefined) queryParams.append("skip", params.skip.toString());
    if (params?.limit !== undefined) queryParams.append("limit", params.limit.toString());
    const url = `${API_BASE_URL}/compliance-rules/results/${queryParams.toString() ? "?" + queryParams.toString() : ""}`;
    return request<PaginatedResponse<any>>(url, undefined, {
      fallback: { items: [], total: 0, page: 1, size: 100, pages: 0 },
      logMessage: "Failed to fetch compliance results",
    });
  },

  async getDeviceComplianceResults(deviceId: string, category?: string): Promise<any> {
    const url = category
      ? `${API_BASE_URL}/compliance-rules/results/device/${deviceId}?category=${category}`
      : `${API_BASE_URL}/compliance-rules/results/device/${deviceId}`;
    return request<any>(url, undefined, { logMessage: `Failed to fetch compliance results for device ${deviceId}` });
  },

  async submitComplianceResult(result: {
    device_id: string;
    rule_id: string;
    status: string;
    evidence?: Record<string, any>;
    actual_value?: string;
  }): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/results/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(result),
    }, { useErrorDetail: true });
  },

  async submitComplianceResultsBatch(results: Array<{
    device_id: string;
    rule_id: string;
    status: string;
    evidence?: Record<string, any>;
    actual_value?: string;
  }>): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/results/batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(results),
    }, { useErrorDetail: true });
  },

  async triggerRemediation(ruleId: string, deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/compliance-rules/${ruleId}/remediate/${deviceId}`, { method: "POST" }, { useErrorDetail: true });
  },

  // User Management (admin only)
  async getUsers(): Promise<User[]> {
    return request<User[]>(`${API_BASE_URL}/users`, undefined, {
      useErrorDetail: true,
      logMessage: "Failed to fetch users",
    });
  },

  async getRolesAndScopes(): Promise<{ roles: string[]; scopes: string[] }> {
    return request<{ roles: string[]; scopes: string[] }>(`${API_BASE_URL}/users/roles`, undefined, {
      useErrorDetail: true,
      logMessage: "Failed to fetch roles and scopes",
    });
  },

  async createAppUser(payload: {
    username: string;
    password: string;
    full_name?: string;
    email?: string;
    role?: string;
    scopes?: string[];
  }): Promise<User> {
    return request<User>(`${API_BASE_URL}/users`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },

  async updateAppUser(
    userId: string,
    payload: {
      full_name?: string;
      email?: string;
      role?: string;
      scopes?: string[];
      is_active?: boolean;
      password?: string;
    }
  ): Promise<User> {
    return request<User>(`${API_BASE_URL}/users/${userId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },

  async deleteAppUser(userId: string): Promise<void> {
    return request<void>(`${API_BASE_URL}/users/${userId}`, { method: "DELETE" }, { useErrorDetail: true });
  },

  // ---- Asset Management ----
  async getAssetsDashboard(): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/dashboard`);
  },
  async getAssetsMeta(): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/meta`);
  },
  async listAssets(params?: { q?: string; status?: string; limit?: number }): Promise<any[]> {
    const qs = new URLSearchParams();
    if (params?.q) qs.set("q", params.q);
    if (params?.status) qs.set("status", params.status);
    if (params?.limit) qs.set("limit", String(params.limit));
    const suffix = qs.toString() ? `?${qs}` : "";
    return request<any[]>(`${API_BASE_URL}/assets${suffix}`);
  },
  async getAsset(assetId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}`);
  },
  async createAsset(payload: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async updateAsset(assetId: string, payload: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async deleteAsset(assetId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}`, { method: "DELETE" }, { useErrorDetail: true });
  },
  async getAssetEnrollmentPolicy(): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/enrollment-policy`);
  },
  async updateAssetEnrollmentPolicy(payload: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/enrollment-policy`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async listAssetEnrollmentAttempts(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/assets/enrollment-attempts`);
  },
  async approveAssetEnrollmentAttempt(attemptId: string, payload?: { asset_id?: string }): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/enrollment-attempts/${attemptId}/approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload || {}),
    }, { useErrorDetail: true });
  },
  async rejectAssetEnrollmentAttempt(attemptId: string, remarks?: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/enrollment-attempts/${attemptId}/reject`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ remarks }),
    }, { useErrorDetail: true });
  },
  async createAssetAndApproveAttempt(attemptId: string, payload: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/enrollment-attempts/${attemptId}/create-asset-and-approve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async getAssetDeviceMatches(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/assets/device-matches`);
  },
  async linkAssetDevice(assetId: string, deviceId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/link-device`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ device_id: deviceId }),
    }, { useErrorDetail: true });
  },
  async unlinkAssetDevice(assetId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/unlink-device`, { method: "POST" }, { useErrorDetail: true });
  },
  async downloadAssetImportTemplate(): Promise<Blob> {
    const token = localStorage.getItem("uem_token");
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const tenantId = localStorage.getItem("platform_active_tenant_id");
    if (tenantId) headers["X-Tenant-Id"] = tenantId;
    const res = await fetch(`${API_BASE_URL}/assets/import/template`, { headers });
    if (!res.ok) throw new Error("Failed to download template");
    return res.blob();
  },
  async uploadAssetImport(file: File): Promise<any> {
    const form = new FormData();
    form.append("file", file);
    return request<any>(`${API_BASE_URL}/assets/import`, { method: "POST", body: form }, { useErrorDetail: true });
  },
  async getAssetImportBatch(batchId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/import/${batchId}`);
  },
  async confirmAssetImport(batchId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/import/${batchId}/confirm`, { method: "POST" }, { useErrorDetail: true });
  },
  async changeAssetStatus(
    assetId: string,
    status: string,
    opts?: { remarks?: string; status_detail?: string }
  ): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/status`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        status,
        remarks: opts?.remarks,
        status_detail: opts?.status_detail,
      }),
    }, { useErrorDetail: true });
  },
  async assignAsset(
    assetId: string,
    payload: {
      assigned_user_id?: string | null;
      assigned_person_name?: string;
      assigned_employee_id?: string;
      department_name?: string;
      location_name?: string;
      site_branch?: string;
      remarks?: string;
      assignment_reason: string;
      assignment_reason_detail?: string;
    }
  ): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async logAssetRepair(
    assetId: string,
    payload: {
      remarks: string;
      vendor?: string;
      cost?: number | null;
      performed_on?: string;
      set_under_repair?: boolean;
      repair_type?: string;
      repair_type_detail?: string;
      spares_cost?: number | null;
      service_cost?: number | null;
      gst_percent?: number | null;
      gst_amount?: number | null;
    }
  ): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/repairs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async renewAssetWarranty(
    assetId: string,
    payload: {
      warranty_end_date: string;
      warranty_start_date?: string;
      vendor?: string;
      invoice_no?: string;
      remarks?: string;
    }
  ): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/warranty-renewals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }, { useErrorDetail: true });
  },
  async allowAssetEnrollment(assetId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/allow-enrollment`, { method: "POST" });
  },
  async blockAssetEnrollment(assetId: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/assets/${assetId}/block-enrollment`, { method: "POST" });
  },

  // ---- Software Metering ----
  async getSoftwareMeteringPolicy(): Promise<any> {
    return request<any>(`${API_BASE_URL}/software-metering/policy`);
  },
  async updateSoftwareMeteringPolicy(body: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/software-metering/policy`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, { useErrorDetail: true });
  },
  async getSoftwareMeteringDashboard(days = 7): Promise<any> {
    return request<any>(`${API_BASE_URL}/software-metering/dashboard?days=${days}`);
  },
  async getSoftwareMeteringApplications(days = 30): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/software-metering/applications?days=${days}`);
  },
  async getSoftwareMeteringDevices(days = 30): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/software-metering/devices?days=${days}`);
  },
  async getSoftwareMeteringUsers(days = 30): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/software-metering/users?days=${days}`);
  },
  async getSoftwareMeteringReports(days = 30): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/software-metering/reports?days=${days}`);
  },
  async exportSoftwareMetering(days = 30): Promise<void> {
    const blob = await request<Blob>(
      `${API_BASE_URL}/software-metering/export?days=${days}`,
      undefined,
      { asBlob: true, useErrorDetail: true }
    );
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "software-metering.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  },
  async platformSoftwareMeteringCoverage(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/software-metering/platform/coverage`);
  },
  async platformEntitleSoftwareMetering(tenantId: string, entitled: boolean): Promise<any> {
    return request<any>(`${API_BASE_URL}/software-metering/platform/${tenantId}/entitle`, {
      method: "PUT",
      body: JSON.stringify({ entitled }),
    }, { useErrorDetail: true });
  },
  async platformSetSoftwareMeteringCollection(tenantId: string, enabled: boolean): Promise<any> {
    return request<any>(`${API_BASE_URL}/software-metering/platform/${tenantId}/collection`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    }, { useErrorDetail: true });
  },

  // ---- Policies & Compliance (governance domains) ----
  async listWorkstationPolicies(category?: string): Promise<any[]> {
    const q = category ? `?category=${encodeURIComponent(category)}` : "";
    return request<any[]>(`${API_BASE_URL}/governance/workstation-policies${q}`);
  },
  async createWorkstationPolicy(body: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/workstation-policies`, {
      method: "POST",
      body: JSON.stringify(body),
    }, { useErrorDetail: true });
  },
  async listServerBaselines(category?: string): Promise<any[]> {
    const q = category ? `?category=${encodeURIComponent(category)}` : "";
    return request<any[]>(`${API_BASE_URL}/governance/server-baselines${q}`);
  },
  async createServerBaseline(body: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/server-baselines`, {
      method: "POST",
      body: JSON.stringify(body),
    }, { useErrorDetail: true });
  },
  async listGovernanceAssignments(policyDomain?: string): Promise<any[]> {
    const q = policyDomain ? `?policy_domain=${encodeURIComponent(policyDomain)}` : "";
    return request<any[]>(`${API_BASE_URL}/governance/assignments${q}`);
  },
  async createGovernanceAssignment(body: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/assignments`, {
      method: "POST",
      body: JSON.stringify(body),
    }, { useErrorDetail: true });
  },
  async getGovernanceResultsDashboard(view = "all"): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/results/dashboard?view=${encodeURIComponent(view)}`);
  },
  async listGovernanceResults(view = "all"): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/governance/results?view=${encodeURIComponent(view)}`);
  },
  async evaluateGovernanceDevice(deviceId: string): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/governance/results/evaluate/${deviceId}`, { method: "POST" }, { useErrorDetail: true });
  },
  async listGovernanceExceptions(): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/governance/exceptions`);
  },
  async requestGovernanceException(body: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/exceptions`, {
      method: "POST",
      body: JSON.stringify(body),
    }, { useErrorDetail: true });
  },
  async approveGovernanceException(id: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/exceptions/${id}/approve`, { method: "POST" }, { useErrorDetail: true });
  },
  async rejectGovernanceException(id: string): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/exceptions/${id}/reject`, { method: "POST" }, { useErrorDetail: true });
  },
  async listDeviceClassifications(needsReview = false): Promise<any[]> {
    return request<any[]>(`${API_BASE_URL}/governance/devices/classification?needs_review=${needsReview}`);
  },
  async classifyDevice(deviceId: string, body: Record<string, unknown>): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/devices/${deviceId}/classify`, {
      method: "PUT",
      body: JSON.stringify(body),
    }, { useErrorDetail: true });
  },
  async migrateGovernanceLegacy(): Promise<any> {
    return request<any>(`${API_BASE_URL}/governance/migrate-legacy`, { method: "POST" }, { useErrorDetail: true });
  },

  // Agent capability matrix — what the installed agent build can actually do.
  async getAgentCapabilityCatalog(): Promise<AgentCapabilityCatalog> {
    return request<AgentCapabilityCatalog>(`${API_BASE_URL}/agent-capabilities/catalog`);
  },
  async getDeviceCapabilities(deviceId: string): Promise<DeviceCapabilityMatrix> {
    return request<DeviceCapabilityMatrix>(`${API_BASE_URL}/agent-capabilities/devices/${deviceId}`);
  },
  async getAgentCapabilitySummary(capability?: string): Promise<AgentCapabilitySummary> {
    const query = capability ? `?capability=${encodeURIComponent(capability)}` : "";
    return request<AgentCapabilitySummary>(`${API_BASE_URL}/agent-capabilities/summary${query}`);
  },

};
